import { usePrevious } from '@eti/utils';
import { useEffect, useState } from 'react';
import { agencyFields, contactFields } from '../constants/formFields';
import type { PhoneType } from '../components/contact-form/Phone';

const { EMERGENCY_PHONE, PHONE } = contactFields;
const { AGENT_PHONE } = agencyFields;

const useTriggerInitialAsyncFormValidation = (
  triggerPhoneAsyncValidation: (phoneNumber: string, phoneType: PhoneType) => Promise<void>,
  triggerEmailAddressAsyncValidation: (email: string) => Promise<void>,
  triggerAgentPhoneAsyncValidation?: (phoneNumber: string, phoneType: PhoneType) => Promise<void>,
  triggerAgentEmailAddressAsyncValidation?: (email: string) => Promise<void>,
  agentEmailAddress?: string,
  agentPhoneNumber?: string,
  emergencyPhoneNumber?: string,
  emailAddress?: string,
  phoneNumber?: string,
) => {
  const [shouldValidateAgentPhoneNumber, setShouldValidateAgentPhoneNumber] = useState(
    Boolean(agentPhoneNumber),
  );
  const [shouldValidateAgentEmail, setShouldValidateAgentEmail] = useState(
    Boolean(agentEmailAddress),
  );
  const [shouldValidatePhoneNumber, setShouldValidatePhoneNumber] = useState(Boolean(phoneNumber));
  const [shouldValidateEmail, setShouldValidateEmail] = useState(Boolean(emailAddress));
  const [shouldValidateEmergencyPhoneNumber, setShouldValidateEmergencyPhoneNumber] = useState(
    Boolean(emergencyPhoneNumber),
  );

  const prevAgentEmailAddress = usePrevious(agentEmailAddress);
  const prevAgentPhoneNumber = usePrevious(agentPhoneNumber);
  const prevEmailAddress = usePrevious(emailAddress);
  const prevEmergencyPhoneNumber = usePrevious(emergencyPhoneNumber);
  const prevPhoneNumber = usePrevious(phoneNumber);

  useEffect(() => {
    const triggerAsyncFormValidation = async () => {
      const validationTasks = [
        async () => {
          if (shouldValidatePhoneNumber && phoneNumber && phoneNumber !== prevPhoneNumber) {
            setShouldValidatePhoneNumber(false);
            await triggerPhoneAsyncValidation(phoneNumber, PHONE);
          }
        },
        async () => {
          if (shouldValidateEmail && emailAddress && emailAddress !== prevEmailAddress) {
            setShouldValidateEmail(false);
            await triggerEmailAddressAsyncValidation(emailAddress);
          }
        },
        async () => {
          if (
            shouldValidateEmergencyPhoneNumber &&
            emergencyPhoneNumber &&
            emergencyPhoneNumber !== prevEmergencyPhoneNumber
          ) {
            setShouldValidateEmergencyPhoneNumber(false);
            await triggerPhoneAsyncValidation(emergencyPhoneNumber, EMERGENCY_PHONE);
          }
        },
        async () => {
          if (
            shouldValidateAgentPhoneNumber &&
            agentPhoneNumber &&
            agentPhoneNumber !== prevAgentPhoneNumber &&
            triggerAgentPhoneAsyncValidation
          ) {
            setShouldValidateAgentPhoneNumber(false);
            await triggerAgentPhoneAsyncValidation(agentPhoneNumber, AGENT_PHONE);
          }
        },
        async () => {
          if (
            shouldValidateAgentEmail &&
            agentEmailAddress &&
            agentEmailAddress !== prevAgentEmailAddress &&
            triggerAgentEmailAddressAsyncValidation
          ) {
            setShouldValidateAgentEmail(false);
            await triggerAgentEmailAddressAsyncValidation(agentEmailAddress);
          }
        },
      ];

      // Execute validation tasks in sequence, waiting for each one to finish before beginning the next one
      for (const task of validationTasks) {
        // eslint-disable-next-line no-await-in-loop
        await task();
      }
    };

    triggerAsyncFormValidation();
  }, [
    agentEmailAddress,
    agentPhoneNumber,
    emailAddress,
    emergencyPhoneNumber,
    phoneNumber,
    prevAgentEmailAddress,
    prevAgentPhoneNumber,
    prevEmailAddress,
    prevEmergencyPhoneNumber,
    prevPhoneNumber,
    shouldValidateAgentEmail,
    shouldValidateAgentPhoneNumber,
    shouldValidateEmail,
    shouldValidateEmergencyPhoneNumber,
    shouldValidatePhoneNumber,
    triggerAgentEmailAddressAsyncValidation,
    triggerAgentPhoneAsyncValidation,
    triggerEmailAddressAsyncValidation,
    triggerPhoneAsyncValidation,
  ]);
};

export default useTriggerInitialAsyncFormValidation;
